import { Dispatch, ReactNode, SetStateAction, useState } from "react";
import Button from "@/shared/form/button";
import { CloseOutlined } from "@ant-design/icons";
import Popover from "@/shared/data-display/popover";
import { useAiTextManipulation } from "@/modules/ai/features/manipulate-text/model/use-ai-text-manipulation";
import { ManipulationTypeButtons } from "@/modules/ai/features/manipulate-text/ui/manipulation-type-buttons";
import { Suggestion } from "@/modules/ai/features/manipulate-text/ui/suggestion";
import { PopoverControls } from "@/modules/ai/features/manipulate-text/ui/popover-controls";
import { useTranslations } from "use-intl";
import { AiButton } from "@/modules/ai/features/manipulate-text/ui/ai-button";

export function AiManipulateTextPopover({
  children,
  isOpen,
  setIsOpen,
  value,
  setValue,
}: {
  children: ReactNode;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  value?: string;
  setValue?: (value: string) => void;
}) {
  const t = useTranslations("AI");

  const {
    suggestion,
    setSuggestion,
    cancel,
    manipulationType,
    manipulate,
    loading,
  } = useAiTextManipulation({
    value,
  });

  const close = () => {
    cancel();
    setIsOpen?.(false);
  };

  return (
    <Popover
      open={isOpen}
      onOpenChange={close}
      title={
        <div className={"flex justify-between items-center"}>
          <span>{t("AI manipulation text menu")}</span>
          <Button
            icon={<CloseOutlined />}
            onClick={close}
            type={"text"}
            size={"small"}
          />
        </div>
      }
      placement={"bottomRight"}
      trigger={"click"}
      destroyTooltipOnHide
      content={
        <div className={"xl:w-[400px]"}>
          <ManipulationTypeButtons
            loading={loading}
            manipulationType={manipulationType}
            manipulate={manipulate}
          />

          {suggestion && (
            <div className={"mt-5"}>
              <Suggestion
                suggestion={suggestion}
                setSuggestion={setSuggestion}
              />
            </div>
          )}

          {suggestion && (
            <div className={"flex justify-end gap-2 mt-5"}>
              <PopoverControls
                onOk={() => {
                  setValue?.(suggestion);
                  close();
                }}
                onCancel={close}
              />
            </div>
          )}
        </div>
      }
    >
      {children}
    </Popover>
  );
}

export function AiManipulateTextButton({
  value,
  setValue,
}: {
  value?: string;
  setValue?: (value: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <AiManipulateTextPopover
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      value={value}
      setValue={setValue}
    >
      <AiButton onClick={() => setIsOpen(true)} />
    </AiManipulateTextPopover>
  );
}
